// Pure helpers for editing a curved line's control-point list. All points are
// absolute world coordinates (as stored in `LineObject.curveControls`).

import { curveMidpoint } from './strategies/curved-path-strategy.js';

export interface Pt {
  x: number;
  y: number;
}

/**
 * The controls the line actually draws through: the stored list, or a single
 * implicit control at the midpoint when the line has none yet (so a fresh
 * curved line still shows one drag handle).
 */
export function effectiveCurveControls(
  controls: readonly Pt[] | undefined,
  start: Pt,
  end: Pt,
): Pt[] {
  return controls && controls.length > 0
    ? controls.map((c) => ({ x: c.x, y: c.y }))
    : [curveMidpoint(start, end)];
}

/**
 * Insert a new control at `point`, ordered so the curve does not loop: it goes
 * into the gap of `[start, ...controls, end]` whose segment is nearest `point`.
 * Returns the new control list.
 */
export function insertCurveControl(
  controls: readonly Pt[] | undefined,
  start: Pt,
  end: Pt,
  point: Pt,
): Pt[] {
  const eff = effectiveCurveControls(controls, start, end);
  const verts = [start, ...eff, end];
  let bestGap = 0;
  let bestDist = Infinity;
  for (let i = 0; i + 1 < verts.length; i++) {
    const d = distanceToSegment(point, verts[i]!, verts[i + 1]!);
    if (d < bestDist) {
      bestDist = d;
      bestGap = i;
    }
  }
  eff.splice(bestGap, 0, { x: point.x, y: point.y });
  return eff;
}

/** Remove the control at `index` (a no-op for an out-of-range index). */
export function removeCurveControl(
  controls: readonly Pt[] | undefined,
  index: number,
): Pt[] {
  const eff = (controls ?? []).map((c) => ({ x: c.x, y: c.y }));
  if (index >= 0 && index < eff.length) eff.splice(index, 1);
  return eff;
}

/** Shortest distance from point `p` to the segment `a`–`b`. */
function distanceToSegment(p: Pt, a: Pt, b: Pt): number {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const len2 = abx * abx + aby * aby;
  let t = len2 === 0 ? 0 : ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2;
  t = Math.max(0, Math.min(1, t));
  const dx = p.x - (a.x + t * abx);
  const dy = p.y - (a.y + t * aby);
  return Math.hypot(dx, dy);
}
